import { useCallback, useEffect, useRef, type MutableRefObject, type PointerEvent } from 'react';
import { startStroke } from './markupDrafts';
import { screenToImage, type Size } from './markupGeometry';
import {
    beginDraw,
    beginMove,
    beginResize,
    constrainGesture,
    finishGesture,
    selectTargetAt,
    trackGesture,
    type Gesture
} from './markupGestures';
import type { MarkupAction } from './markupHistory';
import { hitOptionsFor } from './markupHitTest';
import {
    isDrawTool,
    newShapeId,
    type MarkupShape,
    type MarkupTool,
    type Point
} from './markupShapes';
import { strokeWidthFor, type MarkupSize } from './markupStyle';
import { measureLabel } from './textMeasure';

export interface UseMarkupPointerArgs {
    tool: MarkupTool;
    color: string;
    size: MarkupSize;
    image: Size;
    scale: number;
    shapes: readonly MarkupShape[];
    selected: MarkupShape | null;
    gestureRef: MutableRefObject<Gesture | null>;
    scheduleRedraw: () => void;
    dispatch: (action: MarkupAction) => void;
    onSelect: (shape: MarkupShape | null) => void;
    onPress: () => void;
    onHover: (point: Point | null) => void;
}

type CanvasPointerEvent = PointerEvent<HTMLCanvasElement>;

export interface MarkupPointerHandlers {
    onPointerDown: (e: CanvasPointerEvent) => void;
    onPointerMove: (e: CanvasPointerEvent) => void;
    onPointerUp: (e: CanvasPointerEvent) => void;
    onPointerCancel: (e: CanvasPointerEvent) => void;
    onPointerLeave: () => void;
}

interface ClientPoint {
    clientX: number;
    clientY: number;
}

function samplesOf(e: CanvasPointerEvent): ClientPoint[] {
    const native = e.nativeEvent;
    const coalesced =
        typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    return coalesced.length > 0 ? coalesced : [native];
}

export function toImagePoint(
    point: ClientPoint,
    canvas: HTMLCanvasElement,
    scale: number,
    image: Size
): Point {
    const origin = canvas.getBoundingClientRect();
    return screenToImage(
        { x: point.clientX - origin.left, y: point.clientY - origin.top },
        scale,
        image
    );
}

function useShiftTracking(
    gestureRef: MutableRefObject<Gesture | null>,
    scheduleRedraw: () => void
): void {
    useEffect(() => {
        const handleShift = (event: KeyboardEvent) => {
            const gesture = gestureRef.current;
            if (event.key !== 'Shift' || !gesture) return;
            gestureRef.current = constrainGesture(gesture, event.shiftKey);
            scheduleRedraw();
        };
        window.addEventListener('keydown', handleShift);
        window.addEventListener('keyup', handleShift);
        return () => {
            window.removeEventListener('keydown', handleShift);
            window.removeEventListener('keyup', handleShift);
        };
    }, [gestureRef, scheduleRedraw]);
}

// Text placement lives in useMarkupTextClicks; this handles drawing, moving and resizing.
export function useMarkupPointer(args: UseMarkupPointerArgs): MarkupPointerHandlers {
    const { tool, color, size, image, scale, shapes, selected, gestureRef } = args;
    const { scheduleRedraw, dispatch, onSelect, onPress, onHover } = args;
    const pointerRef = useRef<number | null>(null);
    useShiftTracking(gestureRef, scheduleRedraw);

    const beginGesture = useCallback(
        (at: Point): Gesture | null => {
            if (isDrawTool(tool)) {
                const style = { color, width: strokeWidthFor(size, image) };
                return beginDraw(startStroke(tool, newShapeId(), style, at));
            }
            if (tool !== 'select') return null;
            const target = selectTargetAt(shapes, selected, at, hitOptionsFor(scale), measureLabel);
            onSelect(target.type === 'none' ? null : target.shape);
            if (target.type === 'handle')
                return beginResize(target.shape, target.handle.id, at, measureLabel);
            if (target.type === 'shape') return beginMove(target.shape, at, image, measureLabel);
            return null;
        },
        [tool, color, size, image, scale, shapes, selected, onSelect]
    );

    const onPointerDown = useCallback(
        (e: CanvasPointerEvent) => {
            if ((e.pointerType === 'mouse' && e.button !== 0) || pointerRef.current != null) return;
            onPress();
            const gesture = beginGesture(toImagePoint(e, e.currentTarget, scale, image));
            if (!gesture) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            pointerRef.current = e.pointerId;
            gestureRef.current = constrainGesture(gesture, e.shiftKey);
            scheduleRedraw();
        },
        [onPress, beginGesture, scale, image, gestureRef, scheduleRedraw]
    );

    const onPointerMove = useCallback(
        (e: CanvasPointerEvent) => {
            const gesture = gestureRef.current;
            const canvas = e.currentTarget;
            if (!gesture || pointerRef.current !== e.pointerId) {
                if (pointerRef.current == null) onHover(toImagePoint(e, canvas, scale, image));
                return;
            }
            const points = samplesOf(e).map((sample) => toImagePoint(sample, canvas, scale, image));
            gestureRef.current = constrainGesture(trackGesture(gesture, points, scale), e.shiftKey);
            scheduleRedraw();
        },
        [gestureRef, scale, image, scheduleRedraw, onHover]
    );

    const endGesture = useCallback(
        (e: CanvasPointerEvent, keep: boolean) => {
            if (pointerRef.current !== e.pointerId) return;
            const gesture = gestureRef.current;
            pointerRef.current = null;
            gestureRef.current = null;
            const action = keep && gesture ? finishGesture(gesture, scale) : null;
            if (action) dispatch(action);
            else scheduleRedraw();
            if (keep) onHover(toImagePoint(e, e.currentTarget, scale, image));
        },
        [gestureRef, scale, image, dispatch, scheduleRedraw, onHover]
    );

    const onPointerUp = useCallback((e: CanvasPointerEvent) => endGesture(e, true), [endGesture]);
    const onPointerCancel = useCallback(
        (e: CanvasPointerEvent) => endGesture(e, false),
        [endGesture]
    );
    const onPointerLeave = useCallback(() => {
        if (pointerRef.current == null) onHover(null);
    }, [onHover]);

    return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave };
}
